import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

interface TriviaQuestion {
  id: string;
  question_text: string;
  options: string[];
  correct_option_index: number;
  language: string;
  category: string;
  created_at: string;
}

interface TriviaResponse {
  total: number;
  page: number;
  limit: number;
  questions: TriviaQuestion[];
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border toast-enter cursor-pointer min-w-[280px] ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

const ConfirmDialog: React.FC<{
  open: boolean; title: string; message: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}> = ({ open, title, message, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 bg-[#D85A30] hover:bg-[#D85A30]/90 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {loading ? 'Processing...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-gray-50 p-2 rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const LanguageBadge: React.FC<{ lang: string }> = ({ lang }) => {
  const isEn = lang.toLowerCase() === 'en' || lang.toLowerCase() === 'english';
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${isEn ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
      {isEn ? 'English' : 'Sinhala'}
    </span>
  );
};

const CategoryBadge: React.FC<{ cat: string }> = ({ cat }) => (
  <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 capitalize">
    {cat || 'General'}
  </span>
);

const Trivia: React.FC = () => {
  const [questions, setQuestions] = useState<TriviaQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState('All');
  
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);
  const perPage = 20;

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    question_text: '',
    optionA: '', optionB: '', optionC: '', optionD: '',
    correct_option_index: 0,
    language: 'en',
    category: 'general'
  });

  // Delete Confirm State
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => { setToasts((prev) => prev.filter((t) => t.id !== id)); }, 4000);
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: perPage };
      if (languageFilter !== 'All') {
        params.language = languageFilter === 'English' ? 'en' : 'si';
      }
      const res = await adminApi.get<TriviaResponse>('/admin/trivia/questions', { params });
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      addToast('Failed to load trivia questions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, languageFilter, addToast]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ question_text: '', optionA: '', optionB: '', optionC: '', optionD: '', correct_option_index: 0, language: 'en', category: 'general' });
    setIsModalOpen(true);
  };

  const openEditModal = (q: TriviaQuestion) => {
    setEditingId(q.id);
    const opts = q.options || ['', '', '', ''];
    setFormData({
      question_text: q.question_text,
      optionA: opts[0] || '', optionB: opts[1] || '', optionC: opts[2] || '', optionD: opts[3] || '',
      correct_option_index: q.correct_option_index ?? 0,
      language: q.language || 'en',
      category: q.category || 'general'
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        question_text: formData.question_text,
        options: [formData.optionA, formData.optionB, formData.optionC, formData.optionD],
        correct_option_index: formData.correct_option_index,
        language: formData.language,
        category: formData.category
      };

      if (editingId) {
        await adminApi.put(`/admin/trivia/questions/${editingId}`, payload);
        addToast('Question updated successfully.', 'success');
      } else {
        await adminApi.post('/admin/trivia/questions', payload);
        addToast('Question added successfully.', 'success');
      }
      setIsModalOpen(false);
      fetchQuestions();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to save question.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      setIsDeleting(true);
      await adminApi.delete(`/admin/trivia/questions/${deleteConfirm.id}`);
      addToast('Question deleted.', 'success');
      setDeleteConfirm({ open: false, id: '' });
      fetchQuestions();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to delete question.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            Trivia Question Bank
            <span className="px-3 py-1 bg-[#1D9E75]/10 text-[#1D9E75] rounded-full text-sm font-semibold border border-[#1D9E75]/20">
              {total} Questions
            </span>
          </h1>
        </div>

        <div className="flex items-center gap-3 sm:ml-auto">
          <select
            value={languageFilter}
            onChange={(e) => { setLanguageFilter(e.target.value); setPage(1); }}
            className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
          >
            <option value="All">All Languages</option>
            <option value="English">English</option>
            <option value="Sinhala">Sinhala</option>
          </select>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1D9E75] hover:bg-[#15815f] text-white rounded-xl font-bold transition-all shadow-sm shadow-[#1D9E75]/20 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Question
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">Question</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Options</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Answer</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Language</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-5 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({length: 6}).map((_, j) => <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-full"/></td>)}
                  </tr>
                ))
              ) : questions.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400 font-medium text-base">No questions found.</td></tr>
              ) : (
                questions.map(q => (
                  <tr key={q.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                        {q.question_text.length > 80 ? q.question_text.substring(0, 80) + '...' : q.question_text}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500">
                      <div className="line-clamp-2 leading-relaxed">
                        A) {q.options[0]}<br/>
                        B) {q.options[1]}<br/>
                        C) {q.options[2]}<br/>
                        D) {q.options[3]}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-bold shadow-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] inline-block align-middle">
                        {q.options[q.correct_option_index] || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4"><LanguageBadge lang={q.language} /></td>
                    <td className="px-5 py-4"><CategoryBadge cat={q.category} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(q)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#BA7517]/10 text-[#BA7517] hover:bg-[#BA7517]/20 border border-[#BA7517]/20 transition-all active:scale-95"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, id: q.id })}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#D85A30]/10 text-[#D85A30] hover:bg-[#D85A30]/20 border border-[#D85A30]/20 transition-all active:scale-95"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
           <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <span className="text-sm text-gray-500">
                 Showing <span className="font-semibold text-gray-700">{(page - 1) * perPage + 1}</span>–
                 <span className="font-semibold text-gray-700">{Math.min(page * perPage, total)}</span> of <span className="font-semibold text-gray-700">{total}</span>
              </span>
              <div className="flex items-center gap-2">
                 <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Prev</button>
                 <span className="text-sm px-3"><span className="font-bold text-[#085041]">{page}</span> / <span className="text-gray-500">{totalPages}</span></span>
                 <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
              </div>
           </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => !isSubmitting && setIsModalOpen(false)} title={editingId ? 'Edit Question' : 'Add New Question'}>
        <form onSubmit={handleFormSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Question Text</label>
            <textarea
              required
              rows={3}
              value={formData.question_text}
              onChange={(e) => setFormData(p => ({ ...p, question_text: e.target.value }))}
              placeholder="What is the capital of..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Option A</label>
              <input required type="text" value={formData.optionA} onChange={e => setFormData(p => ({ ...p, optionA: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Option B</label>
              <input required type="text" value={formData.optionB} onChange={e => setFormData(p => ({ ...p, optionB: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Option C</label>
              <input required type="text" value={formData.optionC} onChange={e => setFormData(p => ({ ...p, optionC: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Option D</label>
              <input required type="text" value={formData.optionD} onChange={e => setFormData(p => ({ ...p, optionD: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-100 pt-5 mt-2">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Correct Answer</label>
              <select
                value={formData.correct_option_index}
                onChange={e => setFormData(p => ({ ...p, correct_option_index: Number(e.target.value) }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value={0}>Option A</option>
                <option value={1}>Option B</option>
                <option value={2}>Option C</option>
                <option value={3}>Option D</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Language</label>
              <select
                value={formData.language}
                onChange={e => setFormData(p => ({ ...p, language: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="en">English</option>
                <option value="si">Sinhala</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                placeholder="general"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl text-gray-600 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl text-white bg-[#085041] hover:bg-[#063d32] font-bold transition-all shadow-md shadow-[#085041]/20 active:scale-95 disabled:opacity-50 flex items-center justify-center min-w-[120px]"
            >
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : editingId ? 'Save Changes' : 'Add Question'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirms & Toasts */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Question"
        message="Are you sure you want to delete this trivia question? This action cannot be undone."
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '' })}
      />
      <ToastContainer toasts={toasts} onDismiss={id => setToasts(p => p.filter(t => t.id !== id))} />
    </div>
  );
};

export default Trivia;
